import { DateTime } from "luxon";

// ============================================================
// DataList — helpers purs (sans React) du kit de liste.
// Tout ce qui est calculable hors rendu vit ici pour être testé
// unitairement : pagination, filtres actifs, libellés.
// ============================================================

export const DEFAULT_PAGE_SIZES = [25, 50, 100];
export const ELLIPSIS = "…";

const positiveNumber = (value, fallback) =>
  Number(value) > 0 ? Number(value) : fallback;

export function computeNbPages(count, nbItemsByPage) {
  const size = positiveNumber(nbItemsByPage, DEFAULT_PAGE_SIZES[0]);
  const total = positiveNumber(count, 0);
  return Math.max(1, Math.ceil(total / size));
}

// Bornes affichées « 26–50 sur 234 » (1-based ; 0–0 quand la liste est vide).
export function getDisplayedRange(page, nbItemsByPage, count) {
  const total = positiveNumber(count, 0);
  if (total === 0) return { from: 0, to: 0, total: 0 };
  const size = positiveNumber(nbItemsByPage, DEFAULT_PAGE_SIZES[0]);
  const current = positiveNumber(page, 1);
  return {
    from: Math.min((current - 1) * size + 1, total),
    to: Math.min(current * size, total),
    total,
  };
}

const range = (from, to) =>
  Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => from + i);

// Numéros de page à afficher avec ellipses : 1 … 4 5 6 … 10
export function getPageRange(page, nbPages, { siblings = 1, boundaries = 1 } = {}) {
  const total = Math.max(1, positiveNumber(nbPages, 1));
  const current = Math.min(Math.max(1, positiveNumber(page, 1)), total);
  const totalNumbers = siblings * 2 + 3 + boundaries * 2;
  if (total <= totalNumbers) return range(1, total);

  const leftSibling = Math.max(current - siblings, boundaries + 1);
  const rightSibling = Math.min(current + siblings, total - boundaries);
  const showLeftDots = leftSibling > boundaries + 2;
  const showRightDots = rightSibling < total - boundaries - 1;

  if (!showLeftDots && showRightDots) {
    const leftCount = boundaries + siblings * 2 + 2;
    return [...range(1, leftCount), ELLIPSIS, ...range(total - boundaries + 1, total)];
  }
  if (showLeftDots && !showRightDots) {
    const rightCount = boundaries + siblings * 2 + 2;
    return [...range(1, boundaries), ELLIPSIS, ...range(total - rightCount + 1, total)];
  }
  return [
    ...range(1, boundaries),
    ELLIPSIS,
    ...range(leftSibling, rightSibling),
    ELLIPSIS,
    ...range(total - boundaries + 1, total),
  ];
}

// Un filtre est actif s'il porte une valeur non vide différente de sa valeur
// par défaut. Les valeurs venant du DOM sont des chaînes ("0" est actif). Un
// choix multiple (tableau) est actif s'il est non vide et diffère du défaut,
// sans tenir compte de l'ordre.
export function isFilterActive(value, defaultValue) {
  if (Array.isArray(value) || Array.isArray(defaultValue)) {
    const courant = Array.isArray(value) ? value.map(String).sort() : [];
    const defaut = Array.isArray(defaultValue) ? defaultValue.map(String).sort() : [];
    return courant.length > 0 && courant.join("|") !== defaut.join("|");
  }
  if (value === undefined || value === null || value === "" || value === false) {
    return false;
  }
  if (defaultValue === undefined || defaultValue === null) return true;
  return String(value) !== String(defaultValue);
}

export function countActiveFilters(filters = {}, defaults = {}) {
  return Object.keys(filters).filter((name) =>
    isFilterActive(filters[name], defaults[name])
  ).length;
}

export function formatDate(value) {
  const dt = DateTime.fromISO(String(value));
  return dt.isValid ? dt.toFormat("dd/MM/yyyy") : String(value);
}

const libelleOption = (definition, value) => {
  const index = definition.dataIndex || "id";
  const label = definition.dataLabel || "name";
  const option = (definition.options || []).find((o) => String(o[index]) === String(value));
  return option ? option[label] : String(value);
};

// Libellé lisible d'une valeur de filtre, selon la définition du champ.
export function formatFilterValue(definition, value) {
  switch (definition.type) {
    case "checkbox":
      return null;
    case "date":
      return formatDate(value);
    case "select":
    case "segmented":
      return libelleOption(definition, value);
    case "multi":
      return (Array.isArray(value) ? value : [value]).map((v) => libelleOption(definition, v)).join(", ");
    default:
      return String(value);
  }
}

// Période : deux clés de filtre (`from`, `to`) décrites par une seule définition.
export const isDateRange = (definition) => definition.type === "dateRange";

export function formatDateRange(from, to) {
  if (from && to) return `du ${formatDate(from)} au ${formatDate(to)}`;
  if (from) return `à partir du ${formatDate(from)}`;
  if (to) return `jusqu'au ${formatDate(to)}`;
  return "";
}

// Noms des clés de filtre portées par une définition (deux pour une période).
export const filterNames = (definition) =>
  isDateRange(definition) ? [definition.from, definition.to] : [definition.name];

// Chips « Filtres actifs » : une entrée par définition dont la valeur est active.
// Une période fait une seule chip ; `names` liste les clés à remettre à zéro.
export function getActiveFilterChips(filters = {}, definitions = [], defaults = {}) {
  return definitions
    .filter((d) => filterNames(d).some((n) => isFilterActive(filters[n], defaults[n])))
    .map((d) => {
      if (isDateRange(d)) {
        const actif = (n) => (isFilterActive(filters[n], defaults[n]) ? filters[n] : "");
        return {
          name: d.name,
          names: [d.from, d.to],
          label: d.chipLabel || d.label,
          value: formatDateRange(actif(d.from), actif(d.to)),
        };
      }
      return {
        name: d.name,
        label: d.chipLabel || d.label,
        value: formatFilterValue(d, filters[d.name]),
      };
    });
}

// Filtres principaux (`principal: true`) : posés dans la barre de recherche,
// hors du panneau. Les autres restent groupés dans le panneau.
export function splitFilterDefinitions(definitions = []) {
  return {
    principal: definitions.filter((d) => d.principal),
    secondary: definitions.filter((d) => !d.principal),
  };
}

export function groupFilterDefinitions(definitions = [], groups = []) {
  return groups
    .map((g) => ({ ...g, definitions: definitions.filter((d) => d.group === g.id && !d.principal) }))
    .filter((g) => g.definitions.length > 0);
}

// Valeur d'un <input type="date"> (yyyy-MM-dd) à partir d'une date ISO quelconque.
export function toInputDate(value) {
  if (!value) return "";
  const dt = DateTime.fromISO(String(value));
  return dt.isValid ? dt.toFormat("yyyy-MM-dd") : "";
}

export function formatResultCount(
  count,
  { none = "Aucun résultat", one = "résultat", many = "résultats" } = {}
) {
  const total = positiveNumber(count, 0);
  if (total === 0) return none;
  return `${total.toLocaleString("fr-FR")} ${total === 1 ? one : many}`;
}

// Colonnes de DataTable à partir des champs d'un tableau historique
// (`common/Table` : { name, path, render }) : `render` reçoit la ligne entière
// si `path` vaut « * », sinon `row[path]`, exactement comme avant. Un champ
// faux (colonne retirée pour un rôle : `condition && { … }`) est ignoré.
export function columnsFromFields(fields = []) {
  return fields.filter(Boolean).map((field, index) => ({
    key: `${index}-${field.path}`,
    header: field.name,
    render: (row) =>
      field.render ? field.render(field.path === "*" ? row : row[field.path]) : row[field.path],
  }));
}
